"use client";

// Package Imports
import React from 'react';
import { useCallback, useEffect, useState } from "react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import classNames from "classnames";

// Custom Libraries
import { createQueryString } from "@/lib/functions/createQueryString";

// Styles
import baseStyles from "./pagination.module.scss";
import useScreenSize from "@/lib/hooks/screenSize";
const Pagination = ({
  onPageChange,
  pageCount = 23,
  currentPage = 1,
  styles,
}: {
  onPageChange?: (page: number) => void;
  pageCount: number;
  currentPage: number;
  styles?: {
    pagination?: string;
    paginationList?: string;
    paginationItem?: string;
    noClick?: string;
  };
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const screenSize = useScreenSize();
  const defaultCounts = 3;
  const [endCount, setEndCount] = useState(defaultCounts);
  const [surroundCount, setSurroundCount] = useState(defaultCounts);

  const [activePage, setActivePage] = useState(currentPage);
  const [pages, setPages] = useState(Array<number | string>);
  const left = "<";
  const right = ">";
  const elipse = "…";

  const addLeftArrow = (output: Array<number | string>) => {
    if (activePage > 1) {
      output.unshift(left);
    }
  };

  const addRightArrow = (output: Array<number | string>) => {
    if (activePage != output[output.length - 1]) {
      output.push(right);
    }
  };

  const addElipse = (output: Array<number | string>, i: number) => {
    if (i - Number(output[output.length - 1]) > 1) {
      output.push(elipse);
    }
  };

  useEffect(() => {
    if (screenSize.width < 769) {
      setEndCount(1);
      setSurroundCount(1);
    } else {
      setEndCount(defaultCounts);
      setSurroundCount(defaultCounts);
    }
  }, [screenSize.width]);

  const updatePageLinks = useCallback(() => {
    const output: Array<number | string> = [];
    for (let i: number = 1; i <= pageCount; i++) {
      const selected = i == activePage;
      const firstThree = i <= endCount;
      const lastThree = pageCount - i < endCount;
      const threeInFront = i < activePage && activePage - i <= surroundCount;
      const threeBehind = i > activePage && i - activePage <= surroundCount;

      if (selected || firstThree || lastThree || threeInFront || threeBehind) {
        addElipse(output, i);
        output.push(i);
      }
    }

    addLeftArrow(output);
    addRightArrow(output);

    setPages(output);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageCount, endCount]);

  useEffect(() => {
    updatePageLinks();
  }, [activePage, updatePageLinks, endCount]);

  useEffect(() => {
    setActivePage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (onPageChange) {
      return;
    }

    const updateURL = () => {
      if (!router || !pathname) return;
  
      const existingParams = new URLSearchParams(searchParams?.toString() || '');
      const hasPageParam = existingParams.has('page');
      
      if (activePage === 1 && !hasPageParam) {
        // Do nothing, already clean URL
        return;
      }
  
      router.push(
        pathname +
          '?' +
          createQueryString('page', activePage.toString(), existingParams.toString())
      );
    };
  
    updateURL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, activePage, onPageChange]);

  const setPage = (page: number) => {
    setActivePage(page);
    onPageChange?.(page);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const element = e.target as HTMLButtonElement;

    if (isNumber(Number(element.value))) {
      setPage(Number(element.value));
      updatePageLinks();
    }
  };

  const handleShift = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const element = e.target as HTMLButtonElement;
    if (element.value == left) {
      setPage(activePage - 1);
      return;
    }
    setPage(activePage + 1);
  };

  const isNumber = (value: number | string) => {
    if (typeof value === "string") return false;
    return !isNaN(value);
  };

  const isArrow = (value: number | string) => {
    return value == left || value == right;
  };

  return (
    <nav
      aria-label="Pagination navigation"
      className={classNames(baseStyles.pagination, {
        [styles?.pagination || ""]: styles?.pagination || "",
      })}
    >
      <ul
        className={classNames(baseStyles.paginationList, {
          [styles?.paginationList || ""]: styles?.paginationList || "",
        })}
      >
        {pages &&
          pages.map((value, index) => {
            return (
              <li
                key={`page-${index}`}
                className={classNames(baseStyles.paginationItem, {
                  [baseStyles.active]: activePage == value,
                  [baseStyles.noClick]:
                    !isNumber(Number(value)) &&
                    !(value == left || value == right),
                  [styles?.paginationItem || ""]:
                    styles?.paginationItem || false,
                  [styles?.noClick || ""]:
                    (styles?.noClick && !isNumber(Number(value))) || false,
                })}
              >
                <button
                  value={value}
                  onClick={isArrow(value) ? handleShift : handleClick}
                  // hide elipsis button from screen readers
                  aria-hidden={!isNumber(Number(value)) && !(value === left || value === right) ? "true" : undefined}
                  // make elipsis button unfocusable for keyboard nav users
                  disabled={!isNumber(Number(value)) && !(value === left || value === right)}
                  aria-label={
                    value === left
                      ? "Go to the first page"
                      : value === right
                        ? "Go to the last page"
                        : undefined
                  }
                  aria-current={activePage === value ? "true" : undefined}
                >
                  {value}
                </button>
              </li>
            );
          })}
      </ul>
    </nav>
  );
};

export default Pagination;
